//I am keeping the commented code for refresh as we might be adding this feature in next release

import { useCallback, useState } from "react";
import {
  ADDING_COMMENT,
  ADDING_METER_READING,
  ADDING_TIME_ENTRY,
  ADDING_UPDATING_ANALYSIS_CODES,
  COMPLETING_INSPECTION,
  COMPLETING_WO,
  CREATE_WO,
  ISSUE_MATERIAL,
  RETURN_MATERIAL,
  UPDATE_STATUS,
  UPLOADING_FILE,
  UPLOADING_PHOTO,
  useWoOfflineUpdatesStore,
} from "../../store/wo-offline-updates";
import {
  SUBMIT_CYCLE_COUNT,
  useCycleCountOfflineUpdatesStore,
} from "../../store/cycle-count-offline-updates";
import {
  countEntryOfItems,
  CountEntryRequestData,
} from "../../api/CycleCountBatchs";
import {
  getGroupCountRowState,
  toRowStateMap,
} from "../../api/CycleCountGroups";
import useSubmitNewComments from "../work-order/useSubmitNewComments";
import { UploadResult, useDocumentManager } from "../useDocumentManager";
import { postMeterReadings, putMeterReadings } from "../../api/MeterReadings";
import useCompleteInspection from "../work-order/useCompleteInspection";
import useCreateWo from "../work-order/useCreateWo";
import useSaveAnalysisCodes from "../work-order/useSaveAnalysisCodes";
import useAddUpdateTimeEntry from "../work-order/time-entry/useAddUpdateTimeEntry";
import useCompleteWo from "../work-order/useCompleteWo";
import { issueMaterials, returnMaterials } from "../../api/Materials";
// import useGetComments from "../work-order/useGetComments";
// import useGetLaborLogs from "../work-order/useGetLaborLogs";
// import { getExistingCodes } from "../../api/AnalysisCodes";
// import { useWorkOrderStore } from "../../store/wo-order";
import { changeStatus } from "../../api/MobileApproval";
import useGetWorkOrder from "../useGetWorkOrder";
import { uniq } from "lodash";
import useAttachFollowupWo from "../work-order/useAttachFollowupWo";
// import useGetMeterReadingHistory from "../work-order/useGetMeterReadingHistory";

/**
 * Bug 900806: stamp the CURRENT server-owned values onto queued cycle-count items.
 *
 * Offline counts are queued with the values captured at batch-download time. Two
 * of those go stale and each breaks the sync on its own:
 *  - RowVersion: anything that touched the row since download (another device, a
 *    web-side edit) makes the token stale and the server rejects the count with
 *    "modified by another user".
 *  - Variance: a DB computed column the schema metadata does not mark read-only,
 *    so a stale value makes the ORM try to write it and SQL Server rejects the
 *    whole update. Dropping the field instead does not work - it binds as 0 and
 *    still disagrees. (SystemQty is left alone: the server overwrites it from the
 *    bin's on-hand before saving, so a stale one is harmless.)
 *
 * Returns a same-length copy so results still align by index with the posted
 * array. On any failure the original items are returned unchanged - never blank
 * the token, because the server SKIPS the concurrency check when RowVersion is
 * empty (DbDataEdit.CheckRowVersion).
 */
const withCurrentRowState = async (
  items: CountEntryRequestData[]
): Promise<CountEntryRequestData[]> => {
  const countIds = items
    .map((item) => Number(item.CountId))
    .filter((countId) => Number.isFinite(countId));
  if (countIds.length === 0) {
    return items;
  }
  try {
    const response = await getGroupCountRowState(countIds);
    if (response?.isError) {
      return items;
    }
    const rowState = toRowStateMap(response?.data);
    return items.map((item) => {
      const current = rowState[Number(item.CountId)];
      if (!current?.RowVersion) return item;
      return {
        ...item,
        RowVersion: current.RowVersion,
        ...(current.Variance != null ? { Variance: current.Variance } : {}),
      };
    });
  } catch {
    return items;
  }
};

/**
 * Syncs all offline data i.e. data added, updated while user was offline, when
 * user turn off the offline toggle
 * @returns null
 */
export default function useSyncOfflineUpdates() {
  const { submitNewComments } = useSubmitNewComments();
  const { uploadFile, uploadPhoto } = useDocumentManager();
  const { completeInspection } = useCompleteInspection();
  const { addUpdateTimeEntry } = useAddUpdateTimeEntry();
  const { createWo } = useCreateWo();
  const { saveAnalysisCodes } = useSaveAnalysisCodes();
  const { completeWorkOrder } = useCompleteWo();
  const { attachFollowupWo } = useAttachFollowupWo();

  const [isSyncing, setIsSyncing] = useState(false);
  // const { getComments } = useGetComments();
  // const { getLaborLogs } = useGetLaborLogs();
  const { trigger } = useGetWorkOrder();
  // const { getMeterReadingHistory } = useGetMeterReadingHistory();

  // const setAnalysisCodes = useWorkOrderStore(
  //   (state) => state.setWoAnalysisCodes
  // );
  const makeOperationCompletedById = useWoOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );

  const updateStatusById = useWoOfflineUpdatesStore(
    (state) => state.updateStatusById
  );
  const ccUpdateStatusById = useCycleCountOfflineUpdatesStore(
    (state) => state.updateStatusById
  );
  const ccMakeOperationCompletedById = useCycleCountOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );
  const ccUpdateSubmissionRequestObject = useCycleCountOfflineUpdatesStore(
    (state) => state.updateSubmissionRequestObject
  );
  const ccSetSyncCounts = useCycleCountOfflineUpdatesStore(
    (state) => state.setSyncCounts
  );

  const getErrorList = useCallback((responseData: any) => {
    if (!responseData) return [];
    if (responseData.MessageList && Array.isArray(responseData.MessageList))
      return responseData.MessageList.map((d: any) => d.Text);
    if (
      Array.isArray(responseData) &&
      responseData.every((r) => typeof r === "string")
    )
      return responseData;
    if (Array.isArray(responseData))
      return responseData.map((d: any) => d.Text);
    return [responseData.toString()];
  }, []);

  const syncWoComments = useCallback(
    async (id: string) => {
      const comment = useWoOfflineUpdatesStore.getState().getCommentById(id);
      if (!comment) return; //we have not comments to sync

      updateStatusById(comment.id, "uploading");
      const res = await submitNewComments(comment.comment, comment.woNumber);
      if (!res?.isError && res?.data.Success) {
        makeOperationCompletedById(comment.id); //remove from offline store
        // getComments(comment.woNumber); //refresh the comments
      } else {
        updateStatusById(comment.id, "failed", getErrorList(res?.data));
      }
    },
    [
      getErrorList,
      makeOperationCompletedById,
      submitNewComments,
      updateStatusById,
    ]
  );

  const uploadDoneCallback = useCallback(
    async (id: string, result: UploadResult) => {
      if (result.Success) {
        makeOperationCompletedById(id); //remove from offline store
      } else {
        updateStatusById(id, "failed", getErrorList(result.Response.data));
      }
    },
    [getErrorList, makeOperationCompletedById, updateStatusById]
  );

  const syncFiles = useCallback(
    async (id: string) => {
      const file = useWoOfflineUpdatesStore.getState().getFileToUploadById(id);
      if (!file) return;

      updateStatusById(file.id, "uploading");

      await uploadFile({
        ...file,
        showLoadingCallback: () => {},
        uploadDoneCallback: (result) => {
          uploadDoneCallback(file.id, result);
        },
      });
    },
    [updateStatusById, uploadDoneCallback, uploadFile]
  );

  const syncPhoto = useCallback(
    async (id: string) => {
      const photo = useWoOfflineUpdatesStore
        .getState()
        .getPhotoToUploadById(id);
      if (!photo) return; //got no photo to sync
      updateStatusById(photo.id, "uploading");

      await uploadPhoto({
        ...photo,
        showLoadingCallback: () => {},
        uploadDoneCallback: (result) => {
          uploadDoneCallback(photo.id, result);
        },
      });
    },
    [updateStatusById, uploadDoneCallback, uploadPhoto]
  );

  const syncNewMeterReadings = useCallback(
    async (id: string) => {
      const reading = useWoOfflineUpdatesStore
        .getState()
        .getMeterReadingById(id);
      if (!reading) return; //got nothing to sync

      updateStatusById(reading.id, "uploading");

      const response = reading.isUpdate
        ? await putMeterReadings(reading.data)
        : await postMeterReadings(reading.data);

      if (!response.isError) {
        //refresh
        //  await getMeterReadingHistory(reading.woNumber, reading.data.Meter);
        makeOperationCompletedById(reading.id);
      } else
        updateStatusById(reading.id, "failed", getErrorList(response.data));
    },
    [getErrorList, makeOperationCompletedById, updateStatusById]
  );

  const syncWoInspections = useCallback(
    async (id: string) => {
      const inspection = useWoOfflineUpdatesStore
        .getState()
        .getInspectionById(id);
      if (!inspection) return; //no inspections found
      updateStatusById(inspection.id, "uploading");

      const res = await completeInspection(inspection);
      if (!res.isError) makeOperationCompletedById(inspection.id);
      else updateStatusById(inspection.id, "failed", getErrorList(res.data));
    },
    [
      completeInspection,
      getErrorList,
      makeOperationCompletedById,
      updateStatusById,
    ]
  );

  const syncNewWo = useCallback(
    async (id: string, parentOperationId?: string) => {
      const wo = useWoOfflineUpdatesStore.getState().getWoToCreateById(id);
      if (!wo) return;

      updateStatusById(wo.id, "uploading");

      const res = await createWo(wo);
      if (!res.isError && res.data.Success) {
        if (parentOperationId)
          attachFollowupWo(parentOperationId, res.data?.WorkOrderNumber);
        makeOperationCompletedById(wo.id, res.data.WorkOrderNumber);
      } else {
        updateStatusById(wo.id, "failed", getErrorList(res.data));
      }
    },
    [
      attachFollowupWo,
      createWo,
      getErrorList,
      makeOperationCompletedById,
      updateStatusById,
    ]
  );

  // const getCodes = useCallback(
  //   async (workOrderEvents: any, displayWo: string) => {
  //     const dateCreated = workOrderEvents?.DateCreated;
  //     const timeCreated = workOrderEvents?.TimeCreated;
  //     if (dateCreated && timeCreated) {
  //       const existingCodes = await getExistingCodes(dateCreated, timeCreated);
  //       setAnalysisCodes(displayWo, existingCodes || []);
  //     }
  //   },
  //   [setAnalysisCodes]
  // );

  const syncAnalysisCodes = useCallback(
    async (id: string) => {
      const codes = useWoOfflineUpdatesStore.getState().getAnalysisCodeById(id);
      if (!codes) return;

      updateStatusById(codes.id, "uploading");

      const res = await saveAnalysisCodes(codes.codes, codes.workOrderEvents);
      if (!res.isError && res.data.Success)
        makeOperationCompletedById(codes.id);
      else {
        //update CCA
        // getCodes(codes.workOrderEvents, codes.woNumber);
        updateStatusById(codes.id, "failed", getErrorList(res.data));
      }
    },
    [
      getErrorList,
      makeOperationCompletedById,
      saveAnalysisCodes,
      updateStatusById,
    ]
  );

  const syncTimeEntry = useCallback(
    async (id: string) => {
      const entry = useWoOfflineUpdatesStore.getState().getTimeEntryById(id);
      if (!entry) return;

      updateStatusById(entry.id, "uploading");

      const res = await addUpdateTimeEntry(entry, false);
      if (!res.isError && res.data.Success)
        makeOperationCompletedById(entry.id);
      else {
        //refresh the time entry
        // if (entry.RequestObject?.CfWorkOrder)
        //   getLaborLogs(entry.RequestObject.CfWorkOrder);

        updateStatusById(entry.id, "failed", getErrorList(res.data));
      }
    },
    [
      addUpdateTimeEntry,
      getErrorList,
      makeOperationCompletedById,
      updateStatusById,
    ]
  );

  const syncIssues = useCallback(
    async (id: string) => {
      const issue = useWoOfflineUpdatesStore.getState().getIssueById(id);
      if (!issue) return;

      updateStatusById(issue.id, "uploading");

      const res = await issueMaterials(issue);
      const errorMessageList = res.data
        .filter((d: any) => !d.Success)
        .flatMap((f: any) => f.MessageList);

      if (!res.isError && !errorMessageList.length)
        makeOperationCompletedById(issue.id);
      else updateStatusById(issue.id, "failed", getErrorList(errorMessageList));
    },
    [getErrorList, makeOperationCompletedById, updateStatusById]
  );

  const syncReturns = useCallback(
    async (id: string) => {
      const returnData = useWoOfflineUpdatesStore.getState().getReturnById(id);
      if (!returnData) return;

      updateStatusById(returnData.id, "uploading");

      const res = await returnMaterials(returnData);
      const errorMessageList = res.data
        .filter((d: any) => !d.Success)
        .flatMap((f: any) => f.MessageList);
      if (!res.isError && !errorMessageList.length)
        makeOperationCompletedById(returnData.id);
      else
        updateStatusById(
          returnData.id,
          "failed",
          getErrorList(errorMessageList)
        );
    },
    [getErrorList, makeOperationCompletedById, updateStatusById]
  );

  const syncCompleteWo = useCallback(
    async (id: string) => {
      const wo = useWoOfflineUpdatesStore.getState().getWoToCompleteById(id);
      if (!wo) return;

      updateStatusById(wo.id, "uploading");
      const { Confirmation, RequestObject, woNumber } = wo;
      const res = await completeWorkOrder(
        { Confirmation, RequestObject },
        woNumber
      );
      if (!res) {
        //this will only happen when there is some client issue
        updateStatusById(wo.id, "pending");
        return;
      }
      if (!res.isError && res.data.Success) makeOperationCompletedById(wo.id);
      else updateStatusById(wo.id, "failed", getErrorList(res.data));
    },
    [
      completeWorkOrder,
      getErrorList,
      makeOperationCompletedById,
      updateStatusById,
    ]
  );

  const syncWoStatus = useCallback(
    async (id: string) => {
      const status = useWoOfflineUpdatesStore.getState().getStatusById(id);
      if (!status) return;

      updateStatusById(status.id, "uploading");
      const res = await changeStatus(status);
      if (!res) {
        //this will only happen when there is some client issue
        updateStatusById(status.id, "pending");
        return;
      }
      const hasError = res.data.WoResultsList.some(
        (w: any) => !w.WoResult.Success
      );

      if (!res.isError && !hasError) makeOperationCompletedById(status.id);
      else
        updateStatusById(
          status.id,
          "failed",
          getErrorList(
            res.data.WoResultsList.flatMap((w: any) => w.WoResult).at(0)
          )
        );
    },
    [getErrorList, makeOperationCompletedById, updateStatusById]
  );

  const syncCycleCount = useCallback(
    async (id: string) => {
      const store = useCycleCountOfflineUpdatesStore.getState();
      const submission = store.getCycleCountSubmissionById(id);
      if (!submission) return; //nothing to sync

      ccUpdateStatusById(submission.id, "uploading");

      // Bug 900806: a queued offline count carries the RowVersion and Variance
      // captured when the batch was downloaded. Anything that touched those rows
      // since then (another device, a web-side edit, or an earlier item in this
      // very submission) makes both stale, and either one alone is enough for the
      // server to reject the count. We are online now, so re-read the current
      // values and stamp them onto the queued payload before posting.
      const requestObject = await withCurrentRowState(submission.RequestObject);

      // All counted items for the batch go up in ONE call (the API takes an
      // array); the server returns a per-item CountEntryResults array.
      const res = await countEntryOfItems({
        RequestObject: requestObject,
        Confirmation: submission.Confirmation,
      });

      // hard/network failure — nothing was processed, keep the whole batch
      if (res?.isError) {
        ccUpdateStatusById(submission.id, "failed", getErrorList(res?.data));
        return;
      }

      const countResults = res?.data?.CountEntryResults;

      // no per-item detail returned — treat top-level Success as the verdict
      if (!Array.isArray(countResults)) {
        if (res?.data?.Success === false) {
          ccUpdateStatusById(submission.id, "failed", getErrorList(res?.data));
        } else {
          ccMakeOperationCompletedById(submission.id);
        }
        return;
      }

      // partition by per-item Success (results align by index with what was
      // POSTED, so walk requestObject - the token-refreshed copy - and not the
      // stored submission, otherwise retried items keep the stale RowVersion)
      const failedItems: CountEntryRequestData[] = [];
      const errors: string[] = [];
      requestObject.forEach((item, idx) => {
        const result = countResults[idx];
        if (result && result.Success === false) {
          failedItems.push(item);
          (result.MessageList ?? []).forEach(
            (m: any) => m?.Text && errors.push(`${m.Text} (item: ${item.Item})`)
          );
        }
      });
      if (res?.data?.MessageList?.Text) errors.push(res.data.MessageList.Text);

      const totalSubmitted = submission.RequestObject.length;
      if (failedItems.length === 0) {
        // every item accepted by the server
        ccMakeOperationCompletedById(submission.id);
      } else {
        // accept the succeeded items; keep only the failed ones queued so a
        // retry re-sends just those, and surface why they failed
        ccUpdateSubmissionRequestObject(submission.id, failedItems);
        ccSetSyncCounts(
          submission.id,
          totalSubmitted - failedItems.length,
          failedItems.length
        );
        ccUpdateStatusById(
          submission.id,
          "failed",
          errors.length ? errors : getErrorList(res?.data)
        );
      }
    },
    [
      ccMakeOperationCompletedById,
      ccUpdateStatusById,
      ccUpdateSubmissionRequestObject,
      ccSetSyncCounts,
      getErrorList,
    ]
  );

  // live status lookup across BOTH queues (status mutates as we sync)
  const getLiveStatusById = useCallback((id: string) => {
    const woOp = useWoOfflineUpdatesStore
      .getState()
      .operationDetails.find((o) => o.id === id);
    if (woOp) return woOp.status;
    return useCycleCountOfflineUpdatesStore
      .getState()
      .operationDetails.find((o) => o.id === id)?.status;
  }, []);

  const syncOfflineUpdates = useCallback(async () => {
    const workOrdersToRefresh: string[] = [];
    setIsSyncing(true);
    try {
    // merge both offline queues and sync in the order operations were created
    const operations = [
      ...useWoOfflineUpdatesStore.getState().operationDetails,
      ...useCycleCountOfflineUpdatesStore.getState().operationDetails,
    ];
    const sortedOperations = [...operations].sort(
      (a, b) => a.createdDate.getTime() - b.createdDate.getTime()
    );

    for (let i = 0; i < sortedOperations.length; i++) {
      const op = sortedOperations[i];
      const { operation, id, parentOperationId } = op;

      const hasFailedBefore = sortedOperations
        .slice(0, i)
        .some((o) => getLiveStatusById(o.id) === "failed");
      if (hasFailedBefore) {
        //if there is something failed then we will stop syncing anything and everything after that
        break;
      }

      if (operation === ADDING_COMMENT) {
        await syncWoComments(id);
      } else if (operation === UPLOADING_FILE) {
        await syncFiles(id);
      } else if (operation === UPLOADING_PHOTO) {
        await syncPhoto(id);
      } else if (operation === ADDING_METER_READING) {
        await syncNewMeterReadings(id);
      } else if (operation === COMPLETING_INSPECTION) {
        await syncWoInspections(id);
      } else if (operation === CREATE_WO) {
        await syncNewWo(id, parentOperationId);
      } else if (operation === ADDING_UPDATING_ANALYSIS_CODES) {
        await syncAnalysisCodes(id);
      } else if (operation === ADDING_TIME_ENTRY) {
        await syncTimeEntry(id);
      } else if (operation === COMPLETING_WO) {
        await syncCompleteWo(id);
      } else if (operation === RETURN_MATERIAL) {
        await syncReturns(id);
      } else if (operation === ISSUE_MATERIAL) {
        await syncIssues(id);
      } else if (operation === UPDATE_STATUS) {
        await syncWoStatus(id);
      } else if (operation === SUBMIT_CYCLE_COUNT) {
        await syncCycleCount(id);
      }
      // cycle-count ops carry a synthetic reference (not a real WO number), so
      // exclude them from the work-order refresh below
      if (
        operation !== SUBMIT_CYCLE_COUNT &&
        op.woNumber &&
        !workOrdersToRefresh.includes(op.woNumber)
      ) {
        workOrdersToRefresh.push(op.woNumber);
      }
    }
    //refresh all the work orders that has been updated
    uniq(workOrdersToRefresh).forEach((wo) => {
      trigger(wo);
    });
    } finally {
      // always clear the flag so the syncing loader can never get stuck
      setIsSyncing(false);
    }
  }, [
    getLiveStatusById,
    syncAnalysisCodes,
    syncCompleteWo,
    syncCycleCount,
    syncFiles,
    syncIssues,
    syncNewMeterReadings,
    syncNewWo,
    syncPhoto,
    syncReturns,
    syncTimeEntry,
    syncWoComments,
    syncWoInspections,
    syncWoStatus,
    trigger,
  ]);

  return { syncOfflineUpdates, isSyncing };
}
